import React, {useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {ChartPieIcon, DeviceMobileIcon, InboxInIcon, UserAddIcon, UserRemoveIcon} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {InsightFilters, UsersInsightFilters} from "types/OperatorInsights";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import RoleSelect from "components/InsightControls/RoleSelect";
import {Role} from "types/Role";
import OrganicUserRegistrationsChart from "components/Chart/UserRegistrationsChart";
import UserClaimsChart from "components/Chart/UserClaimsChart";
import UserImportsChart from "components/Chart/UserImportsChart";
import ExportButton from "../../../components/ExportButton";

// Helpers ----

/**
 * Calculate the rate between two values as a percentage
 */
function rate(firstValue: number, secondValue: number): string {
  if (secondValue === 0) {
    return '0%'
  }

  return ((firstValue / secondValue) * 100).toFixed(1) + '%'
}

const breadcrumbs = {"Insights": "/", "User Insights": "/insights/users"}

// Component ----

function UserInsights() {
  const [filters, setFilters] = useState<UsersInsightFilters>({...defaultFilters, role: null});

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'users', filters],
    ({signal}) => ngage.insights.users(filters, {signal}),
    {keepPreviousData: true}
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectRole(role: Role | null) {
    setFilters({...filters, role: role?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.usersExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-user-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page
      title="User Insights"
      subTitle='Understand more about users of the app and the operator platform'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <RoleSelect id="select-role" name="role" label="Role" onChange={selectRole}/>
        </InsightControls>
      </Card>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={3}>
          <StatBox
            name="Registered User Accounts"
            icon={UserAddIcon}
            stat={data?.summary.total_claimed_users.toLocaleString()}
            change={data?.summary.new_claimed_users.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of user accounts that have been used at least once, up to ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Unregistered User Accounts"
            icon={UserRemoveIcon}
            stat={data?.summary.total_unclaimed_users.toLocaleString()}
            change={data?.summary.new_unclaimed_users.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of user accounts that have not yet been used, up to ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="User Registration Rate"
            icon={ChartPieIcon}
            stat={data ? rate(data.summary.total_claimed_users, data.summary.total_claimed_users + data.summary.total_unclaimed_users) : '0%'}
            isLoading={isLoading}
            tooltip={`The proportion of user accounts that are being used, as of ${untilDateString}.`}
          />

          <StatBox
            name="Imported User Accounts"
            icon={InboxInIcon}
            stat={data?.summary.total_imported_users.toLocaleString()}
            change={data?.summary.new_imported_users.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of user accounts that were imported from other places (like ticketing providers), up to ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Organic User Accounts"
            icon={DeviceMobileIcon}
            stat={data?.summary.total_organic_users.toLocaleString()}
            change={data?.summary.new_organic_users.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of user accounts that originated from the audience app, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Organic Registration Rate"
            icon={ChartPieIcon}
            stat={data ? rate(data.summary.total_organic_users, data.summary.total_claimed_users) : '0%'}
            isLoading={isLoading}
            tooltip={`The proportion of registered user accounts that are classed as 'organic', as of ${untilDateString}.`}
          />
        </StatList>
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">
        <Card
          heading="Organic User Accounts"
          subheading={`Total new organic users per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <OrganicUserRegistrationsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading="Imported User Accounts"
          subheading={`Total new imported users per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <UserImportsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading="Registered User Accounts"
          subheading={`Total registered users per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <UserClaimsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>
      </div>
    </Page>
  );
}

export default UserInsights;